/**
 * DOM和自定义事件
 * @author luoying
 * @since 17/06/13
 */
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { alias } from './alias';
import { targetIsDescendant as _targetIsDescendant } from './node';
let eventGuid = 0;
const eventListeners = {};
const eventTargets = {};
function getEventGuid(target) {
    for (const guid in eventTargets) {
        if (eventTargets[guid] === target) {
            return guid;
        }
    }
    const guid = 'events_' + eventGuid++;
    eventTargets[guid] = target;
    return guid;
}
export function on(target, type, listener) {
    if (!target)
        return;
    const guid = getEventGuid(target);
    if (!eventListeners[guid]) {
        eventListeners[guid] = {};
    }
    if (!eventListeners[guid][type]) {
        eventListeners[guid][type] = [];
    }
    target.addEventListener(type, listener, false);
    eventListeners[guid][type].push(listener);
}
export function off(target, type, listener) {
    if (!target)
        return;
    const guid = getEventGuid(target);
    const events = eventListeners[guid];
    if (!events)
        return;
    if (!type) {
        for (const type in events) {
            const handlers = events[type];
            for (const h of handlers) {
                target.removeEventListener(type, h);
            }
        }
        eventListeners[guid] = {};
        return;
    }
    const listeners = events[type];
    if (!listeners)
        return;
    if (!listener) {
        for (const handler of listeners) {
            target.removeEventListener(type, handler);
        }
        events[type] = [];
        return;
    }
    for (let i = 0; i < listeners.length; i++) {
        const handler = listeners[i];
        if (handler === listener) {
            target.removeEventListener(type, handler);
            listeners.splice(i, 1);
            break;
        }
    }
}
export function addEventsToDocument(eventMap) {
    Object.keys(eventMap).forEach(key => {
        document.addEventListener(key, eventMap[key], false);
    });
}
export function removeEventsFromDocument(eventMap) {
    Object.keys(eventMap).forEach(key => {
        document.removeEventListener(key, eventMap[key], false);
    });
}
// 检测是否是指定节点的后代节点
export function targetIsDescendant(target, parent) {
    return _targetIsDescendant(target, parent);
}
// 绑定多个事件
export function addListenerMulti(el, s, fn) {
    s.split(/\s+/).forEach(e => {
        el.addEventListener(e, fn, false);
    });
}
// 解绑多个事件
export function removeListenerMulti(el, s, fn) {
    s.split(/\s+/).forEach(e => {
        el.removeEventListener(e, fn, false);
    });
}
// 绑定多个事件，只执行一次
export function addListenerOnce(el, s, fn) {
    const onceListener = e => {
        fn.call(el, e);
        el.removeEventListener(e.type, onceListener);
    };
    s.split(/\s+/).forEach(e => {
        el.addEventListener(e, onceListener, false);
    });
}
/**
 * 自定义事件器
 * @class EventEmitter
 */
export class EventEmitter {
    constructor() {
        /**
         * 最近发布的一条消息
         */
        this.lastEmittedEvents = new Map();
        /**
         * 事件处理器
         */
        this.eventListeners = new Map();
    }
    /**
     * 注册自定义事件
     * @param {EventType} type 事件类型
     * @param {EventListener<E>} listener 事件处理器
     * @param {boolean} [delayOn=false] 是否延迟订阅
     * @memberof EventEmitter
     */
    addListener(type, listener, delayOn = false) {
        if (!type || !listener)
            return;
        let listeners = this.eventListeners.get(type);
        if (!listeners) {
            listeners = [];
            this.eventListeners.set(type, listeners);
        }
        // 相同的事件不会触发多次
        if (listeners.indexOf(listener) >= 0)
            return;
        // 注册回调事件
        listeners.push(listener);
        // 对于延迟订阅者，需要立即发布一次最近发布的消息
        if (delayOn && this.lastEmittedEvents.has(type)) {
            const lastEmittedEvent = this.lastEmittedEvents.get(type);
            listener(lastEmittedEvent);
        }
    }
    /**
     * 注册一次性自定义事件，用完焚毁
     * @param {EventType} type 事件类型
     * @param {EventListener<E>} listener 事件处理器
     * @param {boolean} [delayOn=false] 是否延迟订阅
     * @memberof EventEmitter
     */
    addOnceListener(type, listener, delayOn = false) {
        const onceListener = (evt) => {
            listener(evt);
            this.off(type, onceListener);
        };
        this.on(type, onceListener, delayOn);
    }
    /**
     * 注销自定义事件
     * @param {EventType} [type] 事件类型
     * @param {EventListener<E>} [listener] 事件处理器，当不指定时，表示注销所有该类型的自定义事件
     * @memberof EventEmitter
     */
    removeListener(type, listener) {
        if (!type)
            return this.removeAllListeners();
        if (!listener) {
            this.eventListeners.set(type, []);
            return;
        }
        const listeners = this.listeners(type);
        for (let i = 0; i < listeners.length; i++) {
            const handler = listeners[i];
            if (handler === listener) {
                listeners.splice(i, 1);
                break;
            }
        }
    }
    /**
     * 注销所有自定义事件
     * @memberof EventEmitter
     */
    removeAllListeners() {
        this.lastEmittedEvents.clear();
        this.eventListeners.clear();
    }
    /**
     * 派发自定义事件
     * @alias fire
     * @param {EventType} type 事件类型
     * @param {E} evt 任意事件数据
     * @memberof EventEmitter
     */
    emit(type, evt) {
        if (!type)
            return;
        // 发布消息给当前所有订阅者
        const listeners = this.listeners(type);
        let count = listeners.length;
        // 当通过`once`注册的事件侦听器，在执行之后立即回收，`listeners`数组长度会缩减
        // 为了准确的执行每一个事件侦听器，需要实时根据`listeners`数组长度来迭代事件侦听器
        while (count) {
            const index = listeners.length - count;
            const listener = listeners[index];
            listener(evt);
            count--;
        }
        // 缓存最新的消息
        this.lastEmittedEvents.set(type, evt);
    }
    /**
     * 返回事件处理器列表
     * @param {EventType} type 事件类型
     * @returns {EventListener<E>[]}
     * @memberof EventEmitter
     */
    listeners(type) {
        return this.eventListeners.get(type) || [];
    }
    /**
     * 返回已注册的事件处理器数量
     * @param {EventType} type 事件类型
     * @returns {number}
     * @memberof EventEmitter
     */
    listenerCount(type) {
        return this.listeners(type).length;
    }
}
__decorate([
    alias('on')
], EventEmitter.prototype, "addListener", null);
__decorate([
    alias('once')
], EventEmitter.prototype, "addOnceListener", null);
__decorate([
    alias('off')
], EventEmitter.prototype, "removeListener", null);
__decorate([
    alias('offAll')
], EventEmitter.prototype, "removeAllListeners", null);
__decorate([
    alias('fire')
], EventEmitter.prototype, "emit", null);
let { __globalEvent__ } = window;
if (!__globalEvent__) {
    window.__globalEvent__ = __globalEvent__ = new EventEmitter();
}
/**
 * 唯一的全局自定义事件器对象（全文档唯一）
 */
export const globalEvent = __globalEvent__;
//# sourceMappingURL=events.js.map